import { useState } from 'react';
import { clampQuantity } from './model';

/**
 * One quantity control for both consignment-invoice screens, so they can never disagree about
 * what a quantity may be: a whole number from `min` (1) to `max`, moved with − and +, or typed.
 *
 * `+` stops at `max` because an order line cannot be invoiced for more than it holds that is
 * not already on another invoice. It says so ("Most: 5") rather than going quietly dead, which
 * is what made it look as though only − worked.
 */
export function QuantityStepper(
  { value, min = 1, max, label, onChange }: {
    value: number; min?: number; max: number; label: string; onChange: (quantity: number) => void;
  },
) {
  // While the person is typing, show their keystrokes; otherwise show the value itself.
  const [text, setText] = useState<string | null>(null);
  const atMin = value <= min;
  const atMax = value >= max;

  function change(q: number) {
    setText(null);
    onChange(clampQuantity(q, max, min));
  }

  return (
    <span className="mk-pi-qty-wrap">
      <span className="mk-pi-qty">
        <button
          type="button"
          aria-label={`One fewer ${label}`}
          title={atMin ? `The fewest is ${min}` : undefined}
          disabled={atMin}
          onClick={() => change(value - 1)}
        >−</button>
        <input
          className="mk-fld"
          inputMode="numeric"
          aria-label={`Quantity of ${label}`}
          value={text ?? String(value)}
          onChange={(e) => {
            const typed = e.target.value;
            setText(typed);
            if (/^\d{1,6}$/.test(typed.trim())) {
              const n = Number(typed.trim());
              const held = clampQuantity(n, max, min);
              onChange(held);
              // A typed 0 or a number above the most reads as the nearest allowed one at once.
              if (held !== n) setText(String(held));
            }
          }}
          onBlur={() => setText(null)}
        />
        <button
          type="button"
          aria-label={`One more ${label}`}
          title={atMax ? `The most is ${max}: that is all this order line has left to invoice` : undefined}
          disabled={atMax}
          onClick={() => change(value + 1)}
        >+</button>
      </span>
      <span className="mk-dim mk-pi-max">Most: {max}</span>
    </span>
  );
}
